import type { ReactNode } from 'react';
import type { Band } from '../../lib/format';
import type { Comparison, EosTeam, IssuePriority, RockStatus, Unit } from './eosTypes';

/* ---------------------------------------------------------------------------
   Weeks

   The scorecard's columns are Mondays. These mirror the same three functions in
   `api/src/routes/eos.ts` — deliberately duplicated rather than fetched,
   because the grid needs to label a column before the request comes back, and
   a round trip to find out what week it is would leave the header blank.

   Both sides compute the Monday against Mountain Time, for the reason
   `core.BusinessToday()` exists: a UTC "today" is already tomorrow after 5pm
   here, and on a Sunday evening that shifts the whole grid one column right.
   --------------------------------------------------------------------------- */

/** The business's today as `yyyy-mm-dd`, in Mountain Time. */
export function eosToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** The Monday on or before `iso`, as `yyyy-mm-dd`. */
export function mondayOf(iso: string): string {
  /* Parsed and stepped in UTC so only the date parts move — the local
     constructor would shift the day for anyone east of Greenwich. */
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

/** `Aug 17` — the column heading for a week. */
export function weekLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** The quarter a date falls in, e.g. `2026-Q3`. */
export function eosQuarter(iso: string = eosToday()): string {
  const [year, month] = iso.split('-').map(Number);
  return `${year}-Q${Math.floor(((month ?? 1) - 1) / 3) + 1}`;
}

/** The four quarters around the current one, for the quarter picker. */
export function quarterOptions(): string[] {
  const [year, q] = eosQuarter().split('-Q').map(Number);
  const out: string[] = [];
  for (let offset = -2; offset <= 2; offset++) {
    const absolute = (q ?? 1) - 1 + offset;
    /* Floor division so the previous year's Q4 comes out as Q4, not Q0. */
    const y = (year ?? 0) + Math.floor(absolute / 4);
    const quarter = ((absolute % 4) + 4) % 4 + 1;
    out.push(`${y}-Q${quarter}`);
  }
  return out;
}

/* ---------------------------------------------------------------------------
   Reading a score

   Whether a number is a hit depends on the measurable's goal RULE, not on
   more-is-better: a measurable can be "at most" (tickets reopened) or "exactly"
   (people at the meeting) as easily as "at least".
   --------------------------------------------------------------------------- */

export function isHit(value: number | null | undefined, goal: number, comparison: Comparison): boolean | null {
  if (value === null || value === undefined) return null;
  if (comparison === '<=') return value <= goal;
  if (comparison === '=') return value === goal;
  return value >= goal;
}

/**
 * The score cell's band.
 *
 * A classic EOS scorecard is green and red. This one is not, because the brand
 * has neither — the ladder runs good navy → critical crimson, and a green "on
 * track" cell would be off-brand. See the design system note in
 * `styles/tokens.css`.
 */
export function scoreBand(hit: boolean | null): Band {
  if (hit === null) return 'neutral';
  return hit ? 'good' : 'critical';
}

/** Formats a score or goal the way its unit reads. */
export function formatMeasure(value: number | null | undefined, unit: Unit): string {
  if (value === null || value === undefined) return '—';
  if (unit === '$') {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2,
    });
  }
  if (unit === '%') return `${Number(value.toFixed(1))}%`;
  /* A count of 12 should read "12", not "12.0" — but a rate of 3.4 must keep
     its decimal, so the precision follows the number rather than the unit. */
  return Number(value.toFixed(2)).toLocaleString();
}

/* ---------------------------------------------------------------------------
   Labels and bands
   --------------------------------------------------------------------------- */

export const ROCK_STATUS_LABELS: Record<RockStatus, string> = {
  not_started: 'Not started',
  on_track: 'On track',
  off_track: 'Off track',
  complete: 'Done',
  /* "The quarter ended and this did not land" — a different thing from off
     track, which is a rock still in play. See migration 058. */
  incomplete: 'Incomplete',
};

export function rockBand(status: RockStatus): Band {
  if (status === 'complete') return 'good';
  if (status === 'on_track') return 'good';
  if (status === 'off_track') return 'serious';
  if (status === 'incomplete') return 'critical';
  return 'neutral';
}

export function priorityBand(priority: IssuePriority): Band {
  if (priority === 'high') return 'critical';
  if (priority === 'medium') return 'warning';
  return 'neutral';
}

/* ---------------------------------------------------------------------------
   The team picker

   Every tab in the module is scoped to one team, so the picker lives in the
   page shell above the tabs rather than being repeated in each of them. Hidden
   entirely when there is only one team: a select with one option is furniture.
   --------------------------------------------------------------------------- */

export function TeamPicker({
  teams,
  teamId,
  onChange,
}: {
  teams: EosTeam[];
  teamId: number | null;
  onChange: (id: number) => void;
}) {
  if (teams.length <= 1) return null;
  return (
    <label className="row" style={{ gap: 8, alignItems: 'center' }}>
      <span className="xs subtle">Team</span>
      <select
        className="select"
        value={teamId ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {teams.map((t) => (
          <option key={t.EosTeamId} value={t.EosTeamId}>
            {t.Name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A labelled section inside a tab, with an action on the right. */
export function EosSection({
  title,
  note,
  actions,
  children,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card mt-4">
      <div className="card-head row-between wrap">
        <div>
          <h3>{title}</h3>
          {note && <p className="xs subtle">{note}</p>}
        </div>
        {actions && <div className="row wrap">{actions}</div>}
      </div>
      <div className="card-body">{children}</div>
    </section>
  );
}

/* =========================================================================
   Business unit
   ========================================================================= */

export interface BusinessUnitOption {
  BusinessUnitId: number;
  Name: string;
}

/**
 * Which business unit an item belongs to, for the business review.
 *
 * OPTIONAL EVERYWHERE, and the empty option says why rather than being a
 * blank line: left unset the review still places the item through its owner's
 * department, so the field is a correction rather than a thing to fill in
 * eleven times. Making it required would put a decision in front of somebody
 * adding a to-do in a meeting, which is the moment least suited to it.
 *
 * The options are the units the review has sections for — see the
 * `/eos/business-units` handler for why that is narrower than the directory's
 * full list.
 */
export function BusinessUnitPicker({
  units,
  value,
  onChange,
}: {
  units: BusinessUnitOption[];
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  return (
    <select
      className="select"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    >
      <option value="">From the owner&rsquo;s own unit</option>
      {units.map((u) => (
        <option key={u.BusinessUnitId} value={u.BusinessUnitId}>
          {u.Name}
        </option>
      ))}
    </select>
  );
}
